import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query'
import { IStorefrontSettings } from './Models/StorefrontSettings'
import { applyStyleConfig } from '../Components/Helpers/styleConfig';
import { getStorefrontStyles, resolveSettingsTokens, setStorefrontStyles } from '../Components/Helpers/stylesConfig';
import { IActionResponse } from './Models/ActionResponse';
import { IStorefrontLoginClaim, IStorefrontLoginStart } from './Models/StorefrontLogin';
import { AppDispatch, RootState } from '../Store/Store';
import {
    IFacetOptions,
    ISuggestResults,
    ISuggestionClick,
    ILinkedProduct,
    IProduct,
    IProductCategory,
    ISearchContext,
    ISearchRequest,
    IStorefrontBundle,
    IStorefrontSearchResults
} from './Models/Products';
import { ICart, IBundleSelectionItem, IBundlePriceEstimateResponse, IShippingCalculationResponse, IShippingRequest, ITaxCalculationResponse, ITaxRequest } from './Models/Cart';
import {ISessionDetails, ISessionRedirectResponse} from './Models/SessionDetails';
import {
    ICartUpdateResponse, ICheckoutQuestionsResponse,
    IOrderDetails, IOrderPaymentUrlResponse,
    IOrderPlacementRequest,
    IOrderQuestion,
    IReorderResponse,
    IStartCheckoutResponse
} from './Models/Order';
import { ICollectionResponse, ISearchResponse } from './Models/Wrappers';
import {ICustomerSignupRequest, ILocation} from './Models/Customer';

const LIST = 'LIST';
const TAG_CART = 'cart';
const TAG_FAVORITES = 'favorites';

// Interest-signal params (see ISearchContext): the surface an action originated from, plus the
// search criteria behind it when that surface was search. Every endpoint below that can carry them
// takes optional `source`/`searchContext` args and appends them via this helper — omitted (not just
// empty) when not supplied, so existing callers that don't pass them are unaffected.
const interestParams = (source?: string, searchContext?: ISearchContext): string => {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (searchContext) params.set('searchContext', JSON.stringify(searchContext));
    return params.toString();
};
// Appends interestParams onto a URL that may or may not already carry a query string.
const withInterestParams = (url: string, source?: string, searchContext?: ISearchContext): string => {
    const extra = interestParams(source, searchContext);
    if (!extra) return url;
    return `${url}${url.includes('?') ? '&' : '?'}${extra}`;
};

// The ONE place the storefront settings blob enters the renderer (both settings endpoints funnel
// through here), so it is where the whole styling pipeline is primed, in strict order:
//
//   1. publish the named styles + the palette/font-pack docs to the module registry the style
//      builders resolve `$styleRef` and `$palette:`/`$font:` tokens against;
//   2. apply the unified styleConfig over the legacy style fields (JSON-first);
//   3. resolve palette/font tokens in the typed style fields, so nothing downstream ever sees a
//      raw token — critically including the background-luminance dark/light theme pick in
//      Layout/StorefrontLoginPage, which reads backgroundColor straight off these settings.
//
// Order matters: (3) must follow (2) because a token can be authored inside styleConfig itself, and
// both must follow (1) because they resolve against the registry. Every step is identity-preserving
// when it has nothing to do, so a storefront with no styles gets back the response object untouched.
const transformStorefrontSettings = (response: IStorefrontSettings): IStorefrontSettings => {
    setStorefrontStyles(response?.stylesConfig);
    return resolveSettingsTokens(applyStyleConfig(response), getStorefrontStyles());
};

export const storefrontApi = createApi({
    reducerPath: 'storefront',
    baseQuery: fetchBaseQuery({
        baseUrl: window.config.baseUrl,
        prepareHeaders: async (headers, api) => {
            const anonymousEndpoints = ['storefrontMultiLogin', 'getBasicStorefront', 'storefrontLogin', 'getSession', 'submitCustomerSignup', 'resolveDomain'];
            if (anonymousEndpoints.includes(api.endpoint)) return headers;
            const state = api.getState() as RootState;
            if (state.auth.token)
                headers.append('x-session-token', `${state.auth.token}`);
            else if(api.endpoint !== 'storefrontAnonymousLogin') {
                throw ("No auth token found");
            }
            return headers;
        }
    }),
    tagTypes: [
        TAG_CART,
        TAG_FAVORITES,
    ],
    endpoints: (builder) => ({
        getStorefront: builder.query<IStorefrontSettings, void>({
            query: (arg) => `/storefront/settings`,
            // Styles are read JSON-first: apply the unified styleConfig over the legacy fields so
            // every consumer reads the effective value (legacy fields remain the fallback).
            // Named element style presets (stylesConfig) are published to the module registry the
            // style builders resolve `$styleRef` against — see Components/Helpers/stylesConfig.ts.
            // This runs before the store updates, so the first render already resolves refs.
            transformResponse: (response: IStorefrontSettings) => transformStorefrontSettings(response),
        }),
        getBasicStorefront: builder.query<IStorefrontSettings, {organization: string, storefront: string}>({
            query: (arg) => `/storefront/${arg.organization}/${arg.storefront}`,
            transformResponse: (response: IStorefrontSettings) => transformStorefrontSettings(response),
        }),
        // Anonymous: does this hostname have a default storefront? 404 = no (generic behavior applies).
        resolveDomain: builder.query<{organization: string, storefront: string}, string>({
            query: (host) => `/storefront/resolve-domain?host=${encodeURIComponent(host)}`,
        }),
        storefrontMultiLogin: builder.mutation<IActionResponse, {emailAddress: string, redirect?: string}>({
            query: (arg) => ({
                url: `/storefront/login?redirect=${encodeURIComponent(arg.redirect ?? '')}`,
                method: 'POST',
                body: { emailAddress: arg.emailAddress }
            })
        }),
        storefrontLogin: builder.mutation<IStorefrontLoginStart, {organization: string, storefront: string, emailAddress: string, redirect?: string}>({
            query: (arg) => ({
                url: `/storefront/${arg.organization}/${arg.storefront}/login?redirect=${encodeURIComponent(arg.redirect ?? '')}`,
                method: 'POST',
                body: { emailAddress: arg.emailAddress }
            })
        }),
        // Claims a session with the code from the sign-in email. Scoped to the pendingId this browser
        // was given, so the code alone is not enough.
        storefrontLoginWithCode: builder.mutation<IStorefrontLoginClaim, {organization: string, storefront: string, pendingId: string, code: string}>({
            query: (arg) => ({
                url: `/storefront/${arg.organization}/${arg.storefront}/login/code`,
                method: 'POST',
                body: { pendingId: arg.pendingId, code: arg.code }
            })
        }),
        // What the waiting page polls while the shopper opens the emailed link elsewhere. Returns a
        // token only once that link has been followed.
        getPendingLogin: builder.query<IStorefrontLoginClaim, {organization: string, storefront: string, pendingId: string}>({
            query: (arg) => `/storefront/${arg.organization}/${arg.storefront}/login/pending/${arg.pendingId}`,
        }),
        // Called by the page the emailed link opens, to release the session to the waiting tab.
        confirmStorefrontLogin: builder.mutation<IStorefrontLoginClaim, {organization: string, storefront: string, pendingId: string}>({
            query: (arg) => ({
                url: `/storefront/${arg.organization}/${arg.storefront}/login/confirm/${arg.pendingId}`,
                method: 'POST',
            })
        }),
        storefrontAnonymousLogin: builder.mutation<ISessionRedirectResponse, {organization: string, storefront: string}>({
            query: (arg) => ({
                url: `/storefront/${arg.organization}/${arg.storefront}/nLogin`,
                method: 'POST'
            })
        }),
        getSession: builder.query<ISessionDetails, {token?: string}>({
            query: (arg) => ({
                url: `/storefront/session`,
                headers: arg.token ? { 'x-session-token': `${arg.token}` } : {},
            })
        }),
        searchProducts: builder.query<IStorefrontSearchResults, {request: ISearchRequest}>({
            query: (arg) => ({
                url: `/products/search`,
                method: 'POST',
                body: arg.request,
            }),
            // Only a favorites-filtered search depends on the favorites set — tagging every search
            // would refetch the whole result set on each heart click. A favorites-only view does need
            // it, so unfavoriting from within that view drops the product out of the list.
            providesTags: (_result, _error, arg) => arg.request.favoritesOnly ? [TAG_FAVORITES] : [],
        }),
        // The signed-in shopper's favorited product ids. One cheap lookup drives every heart on the
        // page, so a toggle doesn't have to refetch the products themselves.
        getFavorites: builder.query<string[], void>({
            query: () => `/favorites`,
            providesTags: [TAG_FAVORITES],
        }),
        addFavorite: builder.mutation<void, {productId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/products/${arg.productId}/favorite`, arg.source, arg.searchContext),
                method: 'POST',
            }),
            invalidatesTags: [TAG_FAVORITES],
        }),
        removeFavorite: builder.mutation<void, {productId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/products/${arg.productId}/favorite`, arg.source, arg.searchContext),
                method: 'DELETE',
            }),
            invalidatesTags: [TAG_FAVORITES],
        }),
        // Optional `page` scopes the featured sections to a custom page (the API does the section
        // filtering). Absent/undefined page = the main homepage sections, and the request is
        // byte-identical to before (`/products/featured`, no query string). RTK Query serializes the
        // arg object into the cache key, so per-page results cache independently.
        getFeaturedProducts: builder.query<IStorefrontSearchResults, { page?: string } | void>({
            query: (arg) => {
                const page = arg ? arg.page : undefined;
                return page ? `/products/featured?page=${encodeURIComponent(page)}` : `/products/featured`;
            }
        }),
        // Dismiss a product from the "Recommended For You" row. Optimistically drops the card from the
        // featured cache (rolling back on failure), so the row updates instantly on click.
        dismissRecommendation: builder.mutation<IActionResponse, {productId: string, page?: string}>({
            query: (arg) => ({
                url: `/products/${arg.productId}/dismiss-recommendation`,
                method: 'POST',
            }),
            async onQueryStarted(arg, queryApi) {
                // The patch has to name the SAME cache entry the caller subscribed to, and callers
                // pass an object ({ page: customPage?.id }) rather than undefined — so patching
                // `undefined` here matched no entry at all, on the homepage as well as on custom
                // pages. Mirror the caller's argument instead.
                const patch = queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getFeaturedProducts', { page: arg.page }, (draft) => {
                        draft.sections?.forEach((s) => {
                            if (s.dismissable && s.products) {
                                s.products = s.products.filter((p) => p.id !== arg.productId);
                            }
                        });
                    })
                );
                try { await queryApi.queryFulfilled; } catch { patch.undo(); }
            },
        }),
        getFacetOptions: builder.query<IFacetOptions, {segment?: string, category?: string, subCategory?: string, keywords?: string}>({
            query: (arg) => {
                const params = new URLSearchParams();
                if (arg.segment) params.set('segment', arg.segment);
                if (arg.category) params.set('category', arg.category);
                if (arg.subCategory) params.set('subCategory', arg.subCategory);
                if (arg.keywords) params.set('keywords', arg.keywords);
                const qs = params.toString();
                return `/products/facet-options${qs ? `?${qs}` : ''}`;
            },
        }),
        // Search-bar autocomplete. Deliberately a lazy query: it fires on a debounced keystroke, not on
        // mount, and RTK Query's cache then serves repeat prefixes (backspacing, retyping) with no
        // request at all. The server holds a short Redis cache behind this for the same reason.
        getSuggestions: builder.query<ISuggestResults, {q: string, columns?: string}>({
            // `columns` names what the calling search bar actually renders, so the BFF can skip the
            // product hydration for a bar configured without product cards.
            query: (arg) => `/products/suggest?q=${encodeURIComponent(arg.q)}`
                + (arg.columns ? `&columns=${encodeURIComponent(arg.columns)}` : ''),
        }),
        // Reports that a suggestion was taken, so the nightly build can learn which ones earn their slot.
        // Fire-and-forget: the shopper is already navigating when this goes out, nothing reads the reply,
        // and the request survives the route change because an SPA navigation does not unload the page.
        // Invalidates nothing — it is a write to an analytics index, not to anything rendered.
        logSuggestionClick: builder.mutation<void, ISuggestionClick>({
            query: (body) => ({ url: '/products/suggest/click', method: 'POST', body }),
        }),
        getProduct: builder.query<IProduct, {productId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => withInterestParams(`/products/${arg.productId}`, arg.source, arg.searchContext),
            // Key the cache on product identity only. source/searchContext still ride on the request
            // (the BFF captures them onto the interest signal) but must NOT fragment the cache — otherwise
            // revisiting the same product from a different surface (search vs. section vs. cart) would
            // miss the cache and both double-fetch and double-log a view that's really just a re-render.
            serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) =>
                defaultSerializeQueryArgs({ queryArgs: { productId: queryArgs.productId }, endpointDefinition, endpointName }),
        }),
        getLinkedProducts: builder.query<ILinkedProduct[], {productId: string}>({
            query: (arg) => `/products/${arg.productId}/linked`
        }),
        // "Frequently added together" row — basic product cards (same shape the .product field of
        // getLinkedProducts returns), gated by the storefront's enableFrequentlyAddedTogether setting.
        // The BFF returns an empty array when the feature is off or there's nothing to show.
        getAddedTogether: builder.query<IProduct[], {productId: string, size?: number}>({
            query: (arg) => `/products/${arg.productId}/addedTogether?size=${arg.size ?? 12}`
        }),
        // "More Like This" row — similar-product recommendations (same IProduct[] shape as
        // getAddedTogether), gated by the storefront's enableMoreLikeThis setting. The BFF returns an
        // empty array when the feature is off or there's nothing to show.
        getMoreLikeThis: builder.query<IProduct[], {productId: string, size?: number}>({
            query: (arg) => `/products/${arg.productId}/moreLikeThis?size=${arg.size ?? 12}`
        }),
        getSavedSearchProduct: builder.query<IProduct, {savedSearchId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => withInterestParams(`/savedSearchProducts/${arg.savedSearchId}`, arg.source, arg.searchContext),
            // Same reasoning as getProduct above — key on the saved-search identity only.
            serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName }) =>
                defaultSerializeQueryArgs({ queryArgs: { savedSearchId: queryArgs.savedSearchId }, endpointDefinition, endpointName }),
        }),
        getBundles: builder.query<IStorefrontBundle[], void>({
            query: (arg) => `/bundles`
        }),
        getBundle: builder.query<IStorefrontBundle, {bundleId: string}>({
            query: (arg) => `/bundles/${arg.bundleId}`
        }),
        getCategories: builder.query<IProductCategory[], void>({
            query: () => '/storefront/categories'
        }),
        getCart: builder.query<ICart, void>({
            query: (arg) => `/cart`,
            providesTags: () => [{type: TAG_CART, id: LIST }]
        }),
        addToCart: builder.mutation<ICartUpdateResponse, {productId: string, quantity?: number, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/products/${arg.productId}?quantity=${arg.quantity || 1}`, arg.source, arg.searchContext),
                method: 'POST',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        addSavedSearchToCart: builder.mutation<ICartUpdateResponse, {quantity?: number, savedSearchId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/savedSearchProducts/${arg.savedSearchId}?quantity=${arg.quantity || 1}`, arg.source, arg.searchContext),
                method: 'POST',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        removeFromCart: builder.mutation<ICartUpdateResponse, {productId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/products/${arg.productId}`, arg.source, arg.searchContext),
                method: 'DELETE',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        removeSavedSearchFromCart: builder.mutation<ICartUpdateResponse, {savedSearchId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/savedSearchProducts/${arg.savedSearchId}`, arg.source, arg.searchContext),
                method: 'DELETE',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        updateCartProduct: builder.mutation<ICartUpdateResponse, {productId: string, quantity: number, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/products/${arg.productId}`, arg.source, arg.searchContext),
                method: 'PUT',
                body: { quantity: arg.quantity }
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        updateCartSavedSearchProduct: builder.mutation<ICartUpdateResponse, {savedSearchId: string, quantity: number, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/savedSearchProducts/${arg.savedSearchId}`, arg.source, arg.searchContext),
                method: 'PUT',
                body: { quantity: arg.quantity }
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
            
        }),
        addBundleToCart: builder.mutation<ICartUpdateResponse, {bundleId: string, selection?: IBundleSelectionItem[], quantity?: number, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/bundles/${arg.bundleId}`, arg.source, arg.searchContext),
                method: 'POST',
                body: { selection: arg.selection ?? [], quantity: arg.quantity ?? 1 },
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        removeBundleFromCart: builder.mutation<ICartUpdateResponse, {cartItemId: string, source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/bundles/${arg.cartItemId}`, arg.source, arg.searchContext),
                method: 'DELETE',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        updateCartBundle: builder.mutation<ICartUpdateResponse, {cartItemId: string, quantity: number, selection?: IBundleSelectionItem[], source?: string, searchContext?: ISearchContext}>({
            query: (arg) => ({
                url: withInterestParams(`/cart/bundles/${arg.cartItemId}`, arg.source, arg.searchContext),
                method: 'PUT',
                // selection is only sent when re-configuring from the cart edit panel; a plain quantity
                // change omits it so the backend leaves the existing selections untouched.
                body: { quantity: arg.quantity, ...(arg.selection ? { selection: arg.selection } : {}) }
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    storefrontApi.util.updateQueryData('getCart', undefined, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        // Estimate the configured price for a bundle without committing to cart.
        // POST /bundles/{bundleId}/estimate with { selection, quantity } → { price, originalPrice?, maxQuantity, messages }.
        estimateBundlePrice: builder.mutation<IBundlePriceEstimateResponse, {bundleId: string, selection: IBundleSelectionItem[], quantity: number}>({
            query: (arg) => ({
                url: `/bundles/${arg.bundleId}/estimate`,
                method: 'POST',
                body: { selection: arg.selection, quantity: arg.quantity },
            }),
        }),
        completeCheckout: builder.mutation<IActionResponse, {orderId: string, request: IOrderPlacementRequest}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/complete`,
                method: 'POST',
                body: arg.request,
            }),
            invalidatesTags: [TAG_CART],
        }),
        setOrderQuestions: builder.mutation<IActionResponse, {questions: IOrderQuestion[], orderId: string}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/questions`,
                method: 'POST',
                body:{ questions: arg.questions }
            })
        }),
        startCheckout: builder.mutation<IStartCheckoutResponse, void>({
            query: () => ({
                url: `/cart/checkout`,
                method: 'POST',
            }),
            // When checkout re-conforms drifted bundles server-side, refetch the cart so the updated
            // bundle line items are shown before the shopper checks out again.
            invalidatesTags: (result) => result?.bundleChanged ? [{ type: TAG_CART, id: LIST }] : [],
        }),
        getCheckoutQuestions: builder.query<ICheckoutQuestionsResponse, { orderId: string }>({
            query: (arg) => ({
                url: `/cart/checkoutQuestions/order/${arg.orderId}`
            }),
        }),
        completeStripeCheckout: builder.mutation<IActionResponse, {orderId: string}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/complete/stripe`,
                method: 'POST',
            }),
        }),
        getOrders: builder.query<ISearchResponse<IOrderDetails>, void>({
            query: () => `/orders`,
        }),
        getOrder: builder.query<IOrderDetails, {orderId: string}>({
            query: (arg) => `/orders/${arg.orderId}`,
        }),
        getOrderPaymentUrl: builder.query<IOrderPaymentUrlResponse, {orderId: string}>({
            query: (arg) => `/orders/${arg.orderId}/paymentUrl`,
        }),
        reorder: builder.mutation<IReorderResponse, {orderId: string}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/reorder`,
                method: 'POST',
            }),
            invalidatesTags: [TAG_CART],
        }),
        getLocations: builder.query<ICollectionResponse<ILocation>, void>({
            query: () => `/customer/locations`,
        }),
        getPermissions: builder.query<string[], void>({
            query: () => `/permissions`,
        }),
        calculateTaxes: builder.mutation<ITaxCalculationResponse, {orderId: string, request: ITaxRequest}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/taxes/calculate`,
                method: 'POST',
                body: arg.request,
            }),
        }),
        calculateShipping: builder.mutation<IShippingCalculationResponse, {orderId: string, request: IShippingRequest}>({
            query: (arg) => ({
                url: `/orders/${arg.orderId}/shipping/calculate`,
                method: 'POST',
                body: arg.request,
            }),
        }),
        submitCustomerSignup: builder.mutation<IActionResponse, {storefront: string, organization: string, request: ICustomerSignupRequest}>({
            query: (arg) => ({
                url: `storefront/signup`,
                method: 'POST',
                body: arg.request,
            }),
        })
    })
});

export default storefrontApi

export const {
    useGetStorefrontQuery,
    useGetBasicStorefrontQuery,
    useStorefrontMultiLoginMutation,
    useResolveDomainQuery,
    useStorefrontLoginMutation,
    useStorefrontLoginWithCodeMutation,
    useGetPendingLoginQuery,
    useConfirmStorefrontLoginMutation,
    useGetSessionQuery,
    useSearchProductsQuery,
    useGetFavoritesQuery,
    useAddFavoriteMutation,
    useRemoveFavoriteMutation,
    useGetFacetOptionsQuery,
    useLazyGetSuggestionsQuery,
    useLogSuggestionClickMutation,
    useGetFeaturedProductsQuery,
    useDismissRecommendationMutation,
    useGetProductQuery,
    useGetLinkedProductsQuery,
    useGetAddedTogetherQuery,
    useGetMoreLikeThisQuery,
    useGetSavedSearchProductQuery,
    useLazyGetSavedSearchProductQuery,
    useGetBundlesQuery,
    useGetBundleQuery,
    useGetCartQuery,
    useLazyGetCartQuery,
    useAddToCartMutation,
    useRemoveFromCartMutation,
    useUpdateCartProductMutation,
    useAddBundleToCartMutation,
    useRemoveBundleFromCartMutation,
    useUpdateCartBundleMutation,
    useGetOrdersQuery,
    useGetOrderQuery,
    useReorderMutation,
    useCompleteCheckoutMutation,
    useStartCheckoutMutation,
    useCompleteStripeCheckoutMutation,
    useAddSavedSearchToCartMutation,
    useRemoveSavedSearchFromCartMutation,
    useUpdateCartSavedSearchProductMutation,
    useSetOrderQuestionsMutation,
    useGetLocationsQuery,
    useCalculateTaxesMutation,
    useCalculateShippingMutation,
    useLazyGetPermissionsQuery,
    useStorefrontAnonymousLoginMutation,
    useSubmitCustomerSignupMutation,
    useGetCategoriesQuery,
    useGetCheckoutQuestionsQuery,
    useLazyGetOrderPaymentUrlQuery,
    useEstimateBundlePriceMutation,
} = storefrontApi;